import {
  MEMBER_PRICE_POLICY_DISCOUNTED,
  MEMBER_PRICE_POLICY_DISCOUNTED_PLUS_COUPON,
  CHEAPEST,
} from '@components/Shared/FeeSelector/FeeTypeName/feeTypeConstantNames';
import { calculateAirlineCampaignData } from 'clientState/hooks/useAirlineSteering';

const DAYS_DIVIDING = 24 * 60 * 60 * 1000;
const ESTIMATED_COLLECTION_METHOD = 'ESTIMATED_COLLECTION_METHOD';

const getDateInDays = (date) =>
  Math.floor(new Date(date).getTime() / DAYS_DIVIDING);

const calculateStayDays = (selectedSegments) => {
  let stayDays = 0;
  if (selectedSegments.length > 1) {
    stayDays =
      getDateInDays(selectedSegments[1].departureDate) -
      getDateInDays(selectedSegments[0].arrivalDate);
  }
  return stayDays;
};

const calculateProductName = ({ legs }) => {
  /*
   * TODO: review this logic and simplify.
   *  frontend-api Itinerary has a field "transportTypes" that return the transportTypes without repetitions.
   *  We should not calculate based on segment transportTypes
   *  Then, we can do something like:
   *
   *  if (itinerary.transportTypes.length > 1) {
   *    return 'xfaring';
   *  } else if (itinerary.transportTypes[0] === 'TRAIN') {
   *    return 'train';
   *  }
   *  return 'flight';
   */
  const transportTypes = legs
    .flatMap(({ segments }) => segments)
    .flatMap(({ transportTypes }) => transportTypes);
  const hasFlight = transportTypes.some(
    (transportType) => transportType === 'PLANE'
  );
  const hasTrain = transportTypes.some(
    (transportType) => transportType === 'TRAIN'
  );
  return hasFlight && hasTrain ? 'xfaring' : hasTrain ? 'train' : 'flight';
};

const getItineraryInformation = (itinerary, trackingInfo) => {
  const { highlightedType, campaignConfig, carbonFootprint = {} } = itinerary;
  const hasAirlineCampaignData = calculateAirlineCampaignData(
    trackingInfo,
    itinerary
  );

  const { selectedSegments, segmentIds, flightNumbers, selectedCarriers } =
    itinerary.legs.reduce(
      (result, { segments, segmentKeys }) => {
        const [firstSegment] = segments;
        result.selectedSegments.push(firstSegment);
        result.segmentIds = result.segmentIds.concat(segmentKeys);
        let flightCodes = firstSegment.sections
          .map(({ flightCode }) => flightCode)
          .join(',');
        result.flightNumbers.push(`${segmentKeys[0]},${flightCodes}`);
        result.selectedCarriers = result.selectedCarriers.concat(
          firstSegment.carriers.map(({ id }) => id)
        );
        return result;
      },
      {
        selectedSegments: [],
        segmentIds: [],
        flightNumbers: [],
        selectedCarriers: [],
      }
    );
  const stayDays = {
    days: calculateStayDays(selectedSegments),
    isOneWay: selectedSegments.length === 1,
  };
  const productName = calculateProductName(itinerary);
  const airlineCampaignConfig = {
    hasAirlineCampaign: hasAirlineCampaignData,
  };
  if (campaignConfig) {
    if (hasAirlineCampaignData) {
      campaignConfig.airlineCampaignConfig = airlineCampaignConfig;
    }
  }

  return {
    highlightedType,
    fareItineraryKey: itinerary.id,
    flightNumbers,
    segmentIds,
    selectedCarriers,
    destinationSelected: `${selectedSegments[0].destination.name}, ${selectedSegments[0].destination.cityName}`,
    searchId: itinerary.searchId,
    feeType: itinerary.feeType,
    stayDays,
    productName,
    campaignConfig,
    carbonFootprint,
  };
};

const formatFareInfo = (fare) => {
  if (!fare) {
    return null;
  }
  const { name, price } = fare;
  return {
    fareFamily: name,
    fareFamilyAmount: price.amount,
  };
};

const getPromotedItineraryTypes = (
  highlightedType,
  returningUserDeviceId,
  carbonFootprint
) => {
  const promotedItineraryTypes = [];

  if (highlightedType === 'recommended') {
    promotedItineraryTypes.push('ODI_RATING');
  }

  if (returningUserDeviceId) {
    promotedItineraryTypes.push('PERSONALIZED');
  }
  if (carbonFootprint && carbonFootprint.isEco) {
    promotedItineraryTypes.push('GREENER_CHOICE');
  }

  return promotedItineraryTypes;
};

const createIsPriceWithoutDiscountJustSelected =
  (feeTypes) => (selectedFeeTypeId) =>
    !feeTypes.includes(selectedFeeTypeId);

const getIsPriceWithoutDiscountJustIfPrime =
  createIsPriceWithoutDiscountJustSelected([
    MEMBER_PRICE_POLICY_DISCOUNTED,
    MEMBER_PRICE_POLICY_DISCOUNTED_PLUS_COUPON,
  ]);

const getIsPriceWithoutDiscountWithCheapest =
  createIsPriceWithoutDiscountJustSelected([
    MEMBER_PRICE_POLICY_DISCOUNTED,
    MEMBER_PRICE_POLICY_DISCOUNTED_PLUS_COUPON,
    CHEAPEST,
  ]);

const createGetCreateShoppingCartRequest =
  (isPriceWithoutDiscountSelected) =>
  (
    {
      fareItineraryKey,
      flightNumbers,
      segmentIds,
      highlightedType,
      carbonFootprint,
    },
    { feeTypeId, searchId, fare },
    { returningUserDeviceId },
    isExtraDiscountAvailable
  ) => ({
    fareItineraryKey,
    flightNumbers,
    priceWithoutDiscountSelected: isPriceWithoutDiscountSelected(feeTypeId),
    membershipCouponApplied:
      feeTypeId === MEMBER_PRICE_POLICY_DISCOUNTED_PLUS_COUPON,
    membershipExtraDiscount: {
      wasExtraDiscountAvailable: isExtraDiscountAvailable,
      wasExtraDiscountSelected:
        feeTypeId === MEMBER_PRICE_POLICY_DISCOUNTED_PLUS_COUPON,
    },
    promotedItineraryTypes: getPromotedItineraryTypes(
      highlightedType,
      returningUserDeviceId,
      carbonFootprint
    ),
    extensions: getExtensions(feeTypeId),
    searchId,
    segmentKeys: segmentIds,
    ...formatFareInfo(fare),
  });

const getExtensions = (feeTypeId) => {
  return feeTypeId
    ? [{ name: ESTIMATED_COLLECTION_METHOD, value: feeTypeId }]
    : [];
};

const getCreateShoppingCartRequestWithCheapest =
  createGetCreateShoppingCartRequest(getIsPriceWithoutDiscountWithCheapest);

const getCreateShoppingCartRequestJustPrime =
  createGetCreateShoppingCartRequest(getIsPriceWithoutDiscountJustIfPrime);

const createCreateItinerarySelection =
  (getCreateShoppingCartRequest) =>
  (itinerary, request, trackingInfo, isShowPrimeExtraDiscount = false) => {
    const info = getItineraryInformation(itinerary, trackingInfo);
    const shoppingCartRequest = getCreateShoppingCartRequest(
      info,
      request,
      trackingInfo,
      isShowPrimeExtraDiscount
    );
    const {
      stayDays,
      destinationSelected,
      selectedCarriers,
      productName,
      campaignConfig,
    } = info;
    return {
      info: {
        stayDetails: JSON.stringify(stayDays),
        destinationSelected: destinationSelected,
        selectedCarriers: JSON.stringify(selectedCarriers),
        productName,
        campaignConfig: JSON.stringify(campaignConfig || {}),
      },
      request: shoppingCartRequest,
    };
  };

export const createItinerarySelectionJustPrime = createCreateItinerarySelection(
  getCreateShoppingCartRequestJustPrime
);
export const createItinerarySelectionWithCheapest =
  createCreateItinerarySelection(getCreateShoppingCartRequestWithCheapest);
